import { Navigate, Outlet } from "react-router-dom";
import { toast } from "sonner";

import { useAuthStore } from "@/features/auth/store";

/**
 * Single source of truth for "can this user perform real actions" — mirrors the
 * backend IsEmailVerified gate (Step C) and the suspended/expired-trial gate
 * (Step D). Read-only browsing is always allowed; this only governs actions
 * (create agent, place call, buy, provision numbers, etc.).
 */
export function useFeatureAccess() {
  const user = useAuthStore((s) => s.user);

  const verified = !!user?.email_verified_at;
  const status = user?.tenant?.status;
  const suspended = status === "suspended" || status === "cancelled";
  // A trial tenant is `active`; it's only blocked once the trial window is spent
  // (the daily task then flips it to `suspended`, so this covers the lag too).
  const trialExpired =
    !!user?.tenant?.is_trial && (user?.tenant?.trial_days_remaining ?? 1) <= 0;
  const blockedByAccount = suspended || trialExpired;

  const allowed = verified && !blockedByAccount;
  const reason = !verified
    ? "Please verify your email before doing this."
    : blockedByAccount
      ? "Your trial has ended — choose a plan to continue."
      : "";

  return { allowed, verified, blockedByAccount, reason };
}

/** Run `action` only if the user may act; otherwise surface the reason. */
export function useGuardedAction() {
  const { allowed, reason } = useFeatureAccess();
  return (action: () => void) => {
    if (!allowed) {
      toast.error(reason);
      return;
    }
    action();
  };
}

/**
 * Route backstop for create/builder routes — if a user reaches one directly
 * (typed URL) without access, bounce them with a message instead of letting
 * them fill a whole wizard only to fail on submit.
 */
export function FeatureGuard({ redirectTo = "/dashboard" }: { redirectTo?: string }) {
  const { allowed, reason } = useFeatureAccess();
  if (!allowed) {
    toast.error(reason);
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
}
